import { Component, Suspense, type ErrorInfo, type ReactNode } from "react";
import LoadingSpinner from "@/components/ui/LoadingSpinner";

interface LazyRouteErrorBoundaryState {
  error: Error | null;
  /** A chunk-load error whose one automatic reload is under way. */
  reloading: boolean;
}

// A lazy-imported chunk hash that no longer exists on the CDN (after deploy)
// rejects with one of these messages depending on the browser.
export function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  const msg = error.message;
  return (
    msg.includes("Failed to fetch dynamically imported module") ||
    msg.includes("Importing a module script failed") ||
    msg.includes("error loading dynamically imported module") ||
    error.name === "ChunkLoadError"
  );
}

/*
 * A7Y-5724 — most chunk-load errors are a transient fetch failure of a chunk
 * that still exists (1–3 a day in server_log), not a stale tab after a deploy,
 * so one automatic reload recovers them without a click. The guard is keyed on
 * the failing chunk and lives in sessionStorage, so a chunk that keeps failing
 * reloads once per tab and then shows the Reload screen instead of looping.
 */
const CHUNK_RELOAD_GUARD_PREFIX = "a7y:chunk-reload:";

function chunkReloadGuardKey(error: Error): string {
  // Chromium and Firefox name the chunk URL in the message; Safari does not,
  // so its guard falls back to the message on this page.
  const url = error.message.match(/https?:\/\/\S+/)?.[0];
  return CHUNK_RELOAD_GUARD_PREFIX + (url ?? `${error.message}@${window.location.pathname}`);
}

/**
 * Whether this chunk-load error should trigger the one automatic reload: the
 * guard for its chunk is not yet set. Unreadable storage answers `false` — a
 * reload that cannot be guarded could loop.
 */
function shouldAutoReload(error: Error): boolean {
  if (!isChunkLoadError(error)) return false;
  try {
    return window.sessionStorage.getItem(chunkReloadGuardKey(error)) === null;
  } catch {
    return false;
  }
}

class LazyRouteErrorBoundary extends Component<
  { children: ReactNode },
  LazyRouteErrorBoundaryState
> {
  state: LazyRouteErrorBoundaryState = { error: null, reloading: false };

  static getDerivedStateFromError(error: Error): LazyRouteErrorBoundaryState {
    return { error, reloading: shouldAutoReload(error) };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    if (!isChunkLoadError(error)) {
      console.error("LazyRoute boundary caught render error:", error, info.componentStack);
      return;
    }
    if (!this.state.reloading) return;
    try {
      window.sessionStorage.setItem(chunkReloadGuardKey(error), String(Date.now()));
    } catch {
      // The guard could not be written, so the reload could not be bounded.
      this.setState({ reloading: false });
      return;
    }
    window.location.reload();
  }

  private handleReload = () => {
    window.location.reload();
  };

  render(): ReactNode {
    const { error, reloading } = this.state;
    if (!error) return this.props.children;
    if (reloading) return <RouteSpinner />;

    const isStale = isChunkLoadError(error);
    return (
      <div className="flex h-screen w-full flex-col items-center justify-center gap-4 px-6 text-center">
        <h2 className="text-lg font-semibold" data-testid={isStale ? "route-stale-build-heading" : "route-error-heading"}>
          {isStale ? "A newer version is available" : "Something went wrong"}
        </h2>
        <p className="max-w-md text-sm text-gray-600">
          {isStale
            ? "Reload the page to load the latest version."
            : "Please reload the page. If the problem persists, contact support."}
        </p>
        <button
          type="button"
          onClick={this.handleReload}
          className="rounded bg-black px-4 py-2 text-sm text-white hover:bg-gray-800"
        >
          Reload
        </button>
      </div>
    );
  }
}

function RouteSpinner() {
  return (
    <div className="flex h-screen w-full items-center justify-center">
      <LoadingSpinner size={48} />
    </div>
  );
}

export default function LazyRoute({ children }: { children: ReactNode }) {
  return (
    <LazyRouteErrorBoundary>
      <Suspense fallback={<RouteSpinner />}>
        {children}
      </Suspense>
    </LazyRouteErrorBoundary>
  );
}
